import { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Paperclip, X, Smile, FileText, UserPlus, MapPin, QrCode, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react';
import { GravadorAudio } from './GravadorAudio';
import { ModalEnviarContato } from './ModalEnviarContato';
import { ModalEnviarLocalizacao } from './ModalEnviarLocalizacao';
import type { Contato, Mensagem } from '../lib/tipos';

interface ArquivoLocal {
  file: File;
  url: string;
  tipo: 'image' | 'video' | 'document';
  falhou?: boolean;
}

interface Props {
  onEnviarTexto: (texto: string) => void | Promise<void>;
  // Devolve `false` quando o envio falha (a Composer mantem o anexo marcado pra reenviar);
  // void/true = enviado com sucesso.
  onEnviarMidia: (file: File, legenda?: string) => boolean | void | Promise<boolean | void>;
  onEnviarAudio: (blob: Blob) => void | Promise<void>;
  responderA: Mensagem | null;
  onCancelarResposta: () => void;
  arquivosArrastados?: File[];
  onArquivosConsumidos?: () => void;
  conversaId?: number | string | null;
  mostrarBotaoProduto?: boolean;
  /** Nome do contato — vai pro titulo da tela de envio de material (DA-682). */
  nomeContato?: string | null;
  /** Liga o bloco SOBRE O CLIENTE na tela de envio de material (nome + etiqueta, 24/09/2026). */
  contatoId?: number | null;
  onContatoAtualizado?: (c: Contato) => void;
  /**
   * Contato e localizacao — os dois so existem no WhatsApp.
   *
   * ⛔ Na caixa `app` o servidor recusa os dois com 409 antes de enviar qualquer coisa
   * (`bloquearSeCanalSemSuporte`), porque sao tipos proprios e o balao do cliente nao
   * tem onde desenha-los. Eles apareciam assim mesmo, e clicar era erro certo — o avesso
   * do principio 5, a tela oferecendo o que o servidor recusa ([DA-593]).
   *
   * ⚠️ O **Pix nao entra aqui**: ele viaja como imagem com legenda, que o app desenha, e
   * passou a funcionar na caixa `app` na [DA-621]. Governa-lo por esta mesma prop foi o
   * erro de leitura da primeira tentativa — "nao existe no app" valia pros outros dois,
   * nao pra ele.
   */
  mostrarEnviosDoWhatsApp?: boolean;
  onEnviarPix?: () => void | Promise<void>;
  textoInjetado?: string | null;
  onTextoInjetadoConsumido?: () => void;
}

function tipoArquivo(f: File): ArquivoLocal['tipo'] {
  if (f.type.startsWith('image/')) return 'image';
  if (f.type.startsWith('video/')) return 'video';
  return 'document';
}

export function Composer({ onEnviarTexto, onEnviarMidia, onEnviarAudio, responderA, onCancelarResposta, arquivosArrastados, onArquivosConsumidos, conversaId, mostrarBotaoProduto, nomeContato, contatoId, onContatoAtualizado, mostrarEnviosDoWhatsApp = true, onEnviarPix, textoInjetado, onTextoInjetadoConsumido }: Props) {
  const [texto, setTexto] = useState('');
  const [arquivos, setArquivos] = useState<ArquivoLocal[]>([]);
  const [idxAtual, setIdxAtual] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [emojiAberto, setEmojiAberto] = useState(false);
  const [menuClipeAberto, setMenuClipeAberto] = useState(false);
  const navigate = useNavigate();
  const [modalContatoAberto, setModalContatoAberto] = useState(false);
  const [modalLocalizacaoAberto, setModalLocalizacaoAberto] = useState(false);
  const [enviandoPix, setEnviandoPix] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (arquivosArrastados && arquivosArrastados.length) {
      const novos: ArquivoLocal[] = arquivosArrastados.map((f) => ({ file: f, url: URL.createObjectURL(f), tipo: tipoArquivo(f) }));
      setArquivos((p) => [...p, ...novos]);
      setIdxAtual(0);
      onArquivosConsumidos?.();
    }
  }, [arquivosArrastados, onArquivosConsumidos]);

  useEffect(() => { if (responderA && textRef.current) textRef.current.focus(); }, [responderA]);
  useEffect(() => { if (conversaId != null && textRef.current) textRef.current.focus(); }, [conversaId]);

  // Texto injetado de fora (ex.: "Usar como mensagem" da transcricao de audio) cai no campo
  // pra o usuario revisar e enviar — nunca envia sozinho. Anexa numa nova linha se ja ha rascunho.
  useEffect(() => {
    if (textoInjetado == null || textoInjetado === '') return;
    setTexto((p) => (p.trim() ? p + '\n' + textoInjetado : textoInjetado));
    onTextoInjetadoConsumido?.();
    setTimeout(() => {
      const ta = textRef.current;
      if (!ta) return;
      ta.focus();
      ta.selectionStart = ta.selectionEnd = ta.value.length;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    }, 0);
  }, [textoInjetado, onTextoInjetadoConsumido]);

  const autoResize = useCallback(() => {
    const ta = textRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  }, []);

  const removerArquivo = (i: number) => {
    URL.revokeObjectURL(arquivos[i]!.url);
    const proximo = arquivos.filter((_, idx) => idx !== i);
    setArquivos(proximo);
    if (idxAtual >= proximo.length) setIdxAtual(Math.max(0, proximo.length - 1));
  };

  const enviar = useCallback(async () => {
    if (sendingRef.current) return;
    if (arquivos.length > 0) {
      sendingRef.current = true;
      setEnviando(true);
      // Envia um de cada vez; o que falhar fica na area, marcado, pra reenviar.
      const restantes: ArquivoLocal[] = [];
      let primeiroFalhou = false;
      try {
        for (let i = 0; i < arquivos.length; i++) {
          const legenda = i === 0 ? (texto.trim() || undefined) : undefined;
          const ok = (await onEnviarMidia(arquivos[i]!.file, legenda)) !== false;
          if (ok) {
            URL.revokeObjectURL(arquivos[i]!.url);
          } else {
            restantes.push({ ...arquivos[i]!, falhou: true });
            if (i === 0) primeiroFalhou = true;
          }
        }
      } finally {
        setEnviando(false);
        sendingRef.current = false;
      }
      setArquivos(restantes);
      setIdxAtual(0);
      // A legenda viaja junto com o 1o anexo. Se ele foi, o texto ja seguiu -> limpa.
      // Se o 1o falhou, mantem o texto pra reenviar junto na proxima tentativa.
      if (!primeiroFalhou) {
        setTexto('');
        if (textRef.current) textRef.current.style.height = '40px';
      }
      return;
    }
    const t = texto.trim();
    if (!t) return;
    onEnviarTexto(t);
    setTexto('');
    if (textRef.current) textRef.current.style.height = '40px';
  }, [texto, arquivos, onEnviarTexto, onEnviarMidia]);

  const onTeclar = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const onColar = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const f = item.getAsFile();
        if (f) {
          const a: ArquivoLocal = { file: f, url: URL.createObjectURL(f), tipo: 'image' };
          setArquivos((p) => [...p, a]);
        }
        return;
      }
    }
  };

  const onSelecionarArquivos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sel = e.target.files;
    if (!sel || !sel.length) return;
    const novos: ArquivoLocal[] = Array.from(sel).map((f) => ({ file: f, url: URL.createObjectURL(f), tipo: tipoArquivo(f) }));
    setArquivos((p) => [...p, ...novos]);
    e.target.value = '';
  };

  const onEmojiClick = useCallback((data: EmojiClickData) => {
    const ta = textRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const novo = texto.slice(0, start) + data.emoji + texto.slice(end);
      setTexto(novo);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + data.emoji.length; ta.focus(); }, 0);
    } else {
      setTexto((p) => p + data.emoji);
    }
  }, [texto]);

  const atual = arquivos[idxAtual];

  return (
    <div className="border-t bg-white relative">
      {responderA && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-100">
          <div className="flex-1 border-l-2 border-blue-400 pl-2">
            <p className="text-xs font-medium text-blue-600">Respondendo a {responderA.assinatura || 'mensagem'}</p>
            <p className="text-xs text-gray-500 truncate">
              {responderA.tipo === 'image' ? '[Imagem]' : (responderA.texto ?? '').slice(0, 80)}
            </p>
          </div>
          <button onClick={onCancelarResposta} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
      )}

      {arquivos.length > 0 && atual && (
        <div className="px-4 py-2 bg-gray-50 border-b">
          {arquivos.some((f) => f.falhou) && (
            <div
              className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg text-sm font-bold"
              style={{ backgroundColor: '#f97316', color: '#000' }}
            >
              ⚠ Não foi enviado. Clique no botão de enviar pra tentar de novo, ou no X pra descartar.
            </div>
          )}
          <div className="flex items-center gap-3">
            {atual.tipo === 'image' ? (
              <img src={atual.url} alt="" className={`h-16 w-16 object-cover rounded-lg ${atual.falhou ? 'ring-2 ring-orange-500' : ''}`} />
            ) : (
              <div className={`h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 ${atual.falhou ? 'ring-2 ring-orange-500' : ''}`}><FileText size={24} /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-700 truncate">{atual.file.name}</p>
              {atual.falhou
                ? <p className="text-xs font-bold" style={{ color: '#c2410c' }}>Falhou ao enviar</p>
                : <p className="text-xs text-gray-400">{(atual.file.size / 1024).toFixed(0)} KB</p>}
            </div>
            <button onClick={() => removerArquivo(idxAtual)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={16} /></button>
          </div>
          {arquivos.length > 1 && (
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1">
                {arquivos.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => setIdxAtual(i)}
                    className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xs overflow-hidden transition ${f.falhou ? 'border-orange-500' : i === idxAtual ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    {f.tipo === 'image' ? <img src={f.url} alt="" className="w-full h-full object-cover" /> : <FileText size={14} className="text-gray-400" />}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-400">{arquivos.length} arquivo{arquivos.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {emojiAberto && (
        <div className="absolute bottom-16 left-2 z-50">
          <div className="fixed inset-0" onClick={() => setEmojiAberto(false)} />
          <div className="relative">
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              searchPlaceholder="Buscar emoji..."
              width={320}
              height={400}
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
            />
          </div>
        </div>
      )}

      <div className="flex items-end gap-2 px-3 py-2">
        <div className="relative">
          <button
            onClick={() => setMenuClipeAberto((v) => !v)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            title="Anexar"
          >
            <Paperclip size={20} />
          </button>
          {menuClipeAberto && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuClipeAberto(false)} />
              <div className="absolute bottom-10 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px]">
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                  onClick={() => { setMenuClipeAberto(false); fileRef.current?.click(); }}
                >
                  <Paperclip size={15} />
                  Arquivo
                </button>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                  onClick={() => { setMenuClipeAberto(false); navigate('/orcamentos', { state: { fromChat: true, conversaId: conversaId != null ? Number(conversaId) : null } }); }}
                >
                  <ClipboardList size={15} />
                  Orçamentos
                </button>
              </div>
            </>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={onSelecionarArquivos} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.mov,.avi" multiple />
        </div>

        <button onClick={() => setEmojiAberto((v) => !v)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Emoji">
          <Smile size={20} />
        </button>

        {mostrarEnviosDoWhatsApp && conversaId != null && (
          <button
            onClick={() => setModalContatoAberto(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            title="Enviar contato"
          >
            <UserPlus size={20} />
          </button>
        )}

        {mostrarEnviosDoWhatsApp && conversaId != null && (
          <button
            onClick={() => setModalLocalizacaoAberto(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            title="Enviar localização"
          >
            <MapPin size={20} />
          </button>
        )}

        {onEnviarPix && conversaId != null && (
          <button
            onClick={async () => {
              if (enviandoPix) return;
              setEnviandoPix(true);
              try { await onEnviarPix(); } finally { setEnviandoPix(false); }
            }}
            disabled={enviandoPix}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-40"
            title="Enviar Pix (QR Code + CNPJ)"
          >
            <QrCode size={20} />
          </button>
        )}

        {/* ⛔ O botao "Produto" SAIU do chat em 07/10/2026 (pedido dele, com print): o fly
            montado aqui anunciava o preco Pix do cadastro e o orcamento cobrava outro. O fly de
            produto agora sai do ORCAMENTO (caixa "Fly" ao lado do WhatsApp), com o preco dele.
            A tela de material (promocoes prontas + Meus flys) segue aberta pelo aviaozinho da
            aba Contatos do dia. */}

        <textarea
          ref={textRef}
          value={texto}
          onChange={(e) => {
            let v = e.target.value;
            // Auto-capitalize: primeira letra do texto E primeira letra depois de . ! ? ou quebra de linha.
            // So aplica quando o usuario esta digitando no final (length crescendo, ultimo char letra).
            if (v.length > texto.length) {
              const ultimoIdx = v.length - 1;
              const ultimoChar = v[ultimoIdx];
              if (ultimoChar && /[a-zà-ÿ]/.test(ultimoChar)) {
                const antes = v.slice(0, ultimoIdx).replace(/\s+$/, '');
                if (antes.length === 0 || /[.!?\n]$/.test(antes)) {
                  v = v.slice(0, ultimoIdx) + ultimoChar.toUpperCase();
                }
              }
            }
            setTexto(v);
            autoResize();
          }}
          onKeyDown={onTeclar}
          onPaste={onColar}
          placeholder="Digite uma mensagem..."
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
          rows={1}
          className="no-upper flex-1 resize-none px-3 py-2 bg-gray-100 rounded-lg text-sm md:text-sm outline-none focus:ring-2 focus:ring-blue-400 overflow-y-auto"
          style={{ minHeight: '40px', fontSize: '16px' }}
        />

        <GravadorAudio onEnviar={onEnviarAudio} />

        <button
          onClick={enviar}
          disabled={(!texto.trim() && arquivos.length === 0) || enviando}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Send size={18} />
        </button>
      </div>

      {modalContatoAberto && conversaId != null && typeof conversaId === 'number' && (
        <ModalEnviarContato
          conversaId={conversaId}
          onClose={() => setModalContatoAberto(false)}
        />
      )}

      {modalLocalizacaoAberto && conversaId != null && typeof conversaId === 'number' && (
        <ModalEnviarLocalizacao
          conversaId={conversaId}
          onClose={() => setModalLocalizacaoAberto(false)}
        />
      )}
    </div>
  );
}
